export const toApiDatetime = (localDatetime) => {
  if (!localDatetime || localDatetime.length < 16) {
    return undefined;
  }
  const date = localDatetime.slice(0, 10);
  const time = localDatetime.slice(11, 16);
  return `${date} ${time}:00`;
};

export const normalizeMetaJson = (raw) => {
  if (raw == null) {
    return {};
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof raw === "object" ? raw : {};
};

const normalizeTag = (t) => {
  if (!t) {
    return null;
  }
  if (
    t.color &&
    typeof t.color === "object" &&
    t.color.bg &&
    t.color.text
  ) {
    return {
      id: String(t.id),
      name: t.name,
      color: {
        bg: t.color.bg,
        text: t.color.text,
        ...(t.color.theme
          ? { theme: t.color.theme }
          : t.color.className
            ? { theme: t.color.className }
            : {}),
        ...(t.color.className ? { className: t.color.className } : {}),
      },
    };
  }
  if (typeof t.color === "string") {
    return {
      id: String(t.id),
      name: t.name,
      color: { bg: t.color, text: "#797979", theme: "gray" },
    };
  }
  if (t.bg && t.text) {
    return {
      id: String(t.id),
      name: t.name,
      color: { bg: t.bg, text: t.text, ...(t.theme ? { theme: t.theme } : {}) },
    };
  }
  return {
    id: String(t.id),
    name: t.name,
    color: { bg: "#FFE4E4", text: "#FE7F7F", theme: "red" },
  };
};

export const parseTagsFromMeta = (meta) => {
  const raw = meta?.tag;
  if (!raw) {
    return [];
  }
  const list = Array.isArray(raw) ? raw : Object.values(raw);
  return list.map(normalizeTag).filter(Boolean);
};

export const buildMetaJsonFromForm = (tags, targetAudience) => {
  const tag = (tags || []).map((t) => ({
    id: String(t.id),
    name: t.name,
    color: {
      bg: t.color?.bg,
      text: t.color?.text,
      ...(t.color?.theme
        ? { theme: t.color.theme }
        : t.color?.className
          ? { theme: t.color.className }
          : {}),
      ...(t.color?.className ? { className: t.color.className } : {}),
    },
  }));
  const meta = { tag };
  if (targetAudience?.trim()) {
    meta.target_audience = targetAudience.trim();
  }
  return meta;
};

export const mapActivityDetailToFormValues = (detail, helpers) => {
  const { toLocalDatetimeInput } = helpers;
  const meta = normalizeMetaJson(detail.meta_json);
  const tags = parseTagsFromMeta(meta);
  const targetAudience =
    meta.target_audience ?? detail.target_audience ?? "";

  return {
    title: detail.title ?? detail.name ?? "",
    subtitle: detail.subtitle ?? "",
    summary: detail.summary ?? "",
    content: detail.content ?? "",
    location: detail.location ?? "",
    targetAudience,
    tags,

    capacity: detail.capacity != null ? String(detail.capacity) : "",
    almostFullThreshold:
      detail.almost_full_threshold != null
        ? String(detail.almost_full_threshold)
        : "",
    reservedCount:
      detail.reserved_count != null ? String(detail.reserved_count) : "",

    activityStartAt: toLocalDatetimeInput(
      detail.activity_start_at ?? detail.eventStartAt,
    ),
    activityEndAt: toLocalDatetimeInput(
      detail.activity_end_at ?? detail.eventEndAt,
    ),
    registrationStartAt: toLocalDatetimeInput(
      detail.registration_start_at ?? detail.registrationStartAt,
    ),
    registrationEndAt: toLocalDatetimeInput(
      detail.registration_end_at ?? detail.registrationEndAt,
    ),

    isPublished: Boolean(
      detail.is_published ?? detail.publishStatus === "published",
    ),
    publishedAt: toLocalDatetimeInput(detail.published_at ?? detail.publishedAt),
    unpublishedAt: toLocalDatetimeInput(
      detail.unpublished_at ?? detail.unpublishedAt,
    ),

    isPinned: Boolean(detail.is_pinned ?? detail.pinned),
    pinnedAt: toLocalDatetimeInput(detail.pinned_at ?? detail.pinnedAt),
    sortOrder: detail.sort_order != null ? String(detail.sort_order) : "",
  };
};

export const getRegistrationCountStats = (row) => {
  const regRaw =
    row.registrationCount ??
    row.registration_count ??
    row.registered_count ??
    row.registrations_count ??
    row.reserved_count ??
    0;
  const reg = Number(regRaw);
  const registered =
    Number.isFinite(reg) && reg >= 0 ? Math.floor(reg) : 0;

  const capRaw = row.capacity;
  if (capRaw == null || capRaw === "") {
    return { registered, capacity: null };
  }
  const c = Number(capRaw);
  const capacity = Number.isFinite(c) && c > 0 ? c : null;
  return { registered, capacity };
};

export const normalizeActivityListRow = (row) => {
  const stats = getRegistrationCountStats(row);
  const meta = normalizeMetaJson(row.meta_json);
  const targetAudience =
    meta.target_audience ?? row.target_audience ?? "";
  return {
    id: row.id,
    name: row.title ?? "",
    publishStatus: row.is_published ? "published" : "draft",
    isPublished: Boolean(row.is_published),
    eventStartAt: row.activity_start_at,
    eventEndAt: row.activity_end_at,
    publishedAt: row.published_at ?? row.publishedAt ?? null,
    unpublishedAt: row.unpublished_at ?? row.unpublishedAt ?? null,
    registrationStartAt: row.registration_start_at,
    registrationEndAt: row.registration_end_at,
    capacity: stats.capacity,
    registrationCount: stats.registered,
    content: typeof row.content === "string" ? row.content : "",
    location: row.location ?? "",
    targetAudience,
  };
};

export const getPublishMissingFields = (form) => {
  const requiredWhenPublished = [
    "title",
    "targetAudience",
    "location",
    "activityStartAt",
    "activityEndAt",
    "publishedAt",
    "unpublishedAt",
    "capacity",
    "content",
  ];
  return requiredWhenPublished.filter((field) => {
    const value = form[field];
    if (typeof value === "string") {
      if (field === "content") {
        const plain = value
          .replace(/<[^>]*>/g, "")
          .replace(/\s+/g, "")
          .trim();
        return plain === "";
      }
      return value.trim() === "";
    }
    if (Array.isArray(value)) return value.length === 0;
    return value == null;
  });
};

export const listRowToPublishCheckForm = (row) => ({
  title: row.title ?? row.name ?? "",
  targetAudience: row.targetAudience ?? "",
  location: row.location ?? "",
  activityStartAt:
    row.eventStartAt != null && row.eventStartAt !== ""
      ? String(row.eventStartAt)
      : "",
  activityEndAt:
    row.eventEndAt != null && row.eventEndAt !== ""
      ? String(row.eventEndAt)
      : "",
  publishedAt:
    row.publishedAt != null && row.publishedAt !== ""
      ? String(row.publishedAt)
      : "",
  unpublishedAt:
    row.unpublishedAt != null && row.unpublishedAt !== ""
      ? String(row.unpublishedAt)
      : "",
  capacity:
    row.capacity != null && row.capacity !== ""
      ? String(row.capacity)
      : "",
  content: typeof row.content === "string" ? row.content : "",
});

export const getHighlightPublishMissingFields = (form) => {
  const requiredWhenPublished = ["title", "eventType", "description"];
  return requiredWhenPublished.filter((field) => {
    const value = form[field];
    if (field === "description") {
      const plain = String(value || "")
        .replace(/<[^>]*>/g, "")
        .replace(/\s+/g, "")
        .trim();
      return plain === "";
    }
    if (typeof value === "string") return value.trim() === "";
    return value == null;
  });
};

export const highlightListRowToPublishCheckForm = (item) => ({
  title: item?.name ?? "",
  eventType: item?.eventTypeForPublish ?? "",
  description: typeof item?.description === "string" ? item.description : "",
});

export const normalizeRegistrationListResponse = (data) => {
  if (!data) {
    return { rows: [], total: 0 };
  }
  if (Array.isArray(data)) {
    return { rows: data, total: data.length };
  }
  const rows = data.rows ?? data.items ?? [];
  const total = data.total ?? rows.length;
  return { rows, total };
};

export const mapRegistrationApiRowToModal = (r) => {
  const company = r.company_name ?? "";
  const job = r.job_title ?? "";
  const companyOrPosition =
    company && job ? `${company} / ${job}` : company || job || "";
  return {
    id: r.registration_id ?? r.id ?? r.item_id,
    name: r.name ?? "",
    email: r.email ?? "",
    phone: r.phone ?? "",
    companyOrPosition,
    jobTitle: r.job_title ?? "-",
    companyName: r.company_name ?? "-",
    registeredAt: r.registered_at ?? r.submitted_at,
    notes: r.remark ?? r.note ?? "",
  };
};

export const mapRegistrationApiRowToList = (r) => {
  const company = r.company_name ?? "";
  const job = r.job_title ?? "";
  const companyOrPosition =
    company && job ? `${company} / ${job}` : company || job || "";
  return {
    id: r.item_id ?? r.id ?? r.registration_id,
    registrationId: r.registration_id ?? r.id ?? r.item_id,
    eventId: r.activity_id ?? r.event_id ?? null,
    eventName: r.activity_title ?? r.event_title ?? "",
    name: r.name ?? "",
    email: r.email ?? "",
    phone: r.phone ?? "",
    companyOrPosition,
    jobTitle: r.job_title ?? "-",
    companyName: r.company_name ?? "-",
    registeredAt: r.registered_at ?? r.submitted_at,
    notes: r.remark ?? r.note ?? "",
  };
};

export const mapRegistrationByEmailActivityRow = (r) => ({
  id:
    r.activity_id ??
    `${r.activity_title ?? "activity"}-${r.submitted_at ?? Date.now()}`,
  submittedAt: r.submitted_at ?? "",
  name: r.name ?? "",
  activityId: r.activity_id ?? null,
  activityTitle: r.activity_title ?? "",
  activityDatetime: r.activity_datetime ?? "",
  activityLocation: r.activity_location ?? "",
  remark: r.remark ?? "",
});

const toOptionalInt = (v) => {
  if (v === "" || v == null) {
    return undefined;
  }
  const n = Number.parseInt(String(v), 10);
  return Number.isFinite(n) ? n : undefined;
};

export const buildActivityPayload = (form) => {
  const payload = {
    title: form.title.trim(),
    meta_json: buildMetaJsonFromForm(form.tags, form.targetAudience),
  };

  if (form.subtitle?.trim()) {
    payload.subtitle = form.subtitle.trim();
  }
  if (form.summary?.trim()) {
    payload.summary = form.summary.trim();
  }
  if (form.content?.trim()) {
    payload.content = form.content.trim();
  }
  if (form.location?.trim()) {
    payload.location = form.location.trim();
  }

  const capacity = toOptionalInt(form.capacity);
  const almostFullThreshold = toOptionalInt(form.almostFullThreshold);
  const reservedCount = toOptionalInt(form.reservedCount);
  const sortOrder = toOptionalInt(form.sortOrder);

  if (capacity !== undefined) {
    payload.capacity = capacity;
  }
  if (almostFullThreshold !== undefined) {
    payload.almost_full_threshold = almostFullThreshold;
  }
  if (reservedCount !== undefined) {
    payload.reserved_count = reservedCount;
  }

  const aStart = toApiDatetime(form.activityStartAt);
  const aEnd = toApiDatetime(form.activityEndAt);
  const publishedAt = toApiDatetime(form.publishedAt);
  const unpublishedAt = toApiDatetime(form.unpublishedAt);
  const rStart = publishedAt;
  const rEnd = aStart;
  if (aStart) {
    payload.activity_start_at = aStart;
  }
  if (aEnd) {
    payload.activity_end_at = aEnd;
  }
  if (rStart) {
    payload.registration_start_at = rStart;
  }
  if (rEnd) {
    payload.registration_end_at = rEnd;
  }

  payload.is_published = Boolean(form.isPublished);
  if (publishedAt) {
    payload.published_at = publishedAt;
  }
  if (unpublishedAt) {
    payload.unpublished_at = unpublishedAt;
  }

  payload.is_pinned = Boolean(form.isPinned);
  const pinnedAt = toApiDatetime(form.pinnedAt);
  if (form.isPinned && pinnedAt) {
    payload.pinned_at = pinnedAt;
  }
  if (sortOrder !== undefined) {
    payload.sort_order = sortOrder;
  }

  return payload;
};

const pickFirstErrorMessage = (value) => {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = pickFirstErrorMessage(item);
      if (found) return found;
    }
    return "";
  }
  if (value && typeof value === "object") {
    if ("msg" in value) {
      const foundFromMsg = pickFirstErrorMessage(value.msg);
      if (foundFromMsg) return foundFromMsg;
    }
    for (const key of Object.keys(value)) {
      const found = pickFirstErrorMessage(value[key]);
      if (found) return found;
    }
  }
  return "";
};

export const getActivitySaveErrorToast = (err) => {
  const fallback = { title: "儲存失敗請重試", error: true };
  const raw = err?.message;
  if (raw == null || typeof raw !== "string") {
    return fallback;
  }
  try {
    const parsed = JSON.parse(raw);
    const firstMsg = pickFirstErrorMessage(parsed);
    if (firstMsg) {
      return { title: firstMsg, error: true };
    }
  } catch {
    const plainMsg = pickFirstErrorMessage(raw);
    if (plainMsg) {
      return { title: plainMsg, error: true };
    }
  }
  return fallback;
};

const errorMessageMentionsYoutube = (raw) => {
  if (raw == null || typeof raw !== "string" || raw === "") {
    return false;
  }
  if (/youtube/i.test(raw)) {
    return true;
  }
  try {
    const parsed = JSON.parse(raw);
    return /youtube/i.test(JSON.stringify(parsed));
  } catch {
    return false;
  }
};

export const getHighlightSaveErrorToast = (err) => {
  const fallback = { title: "儲存失敗請重試", error: true };
  const raw = err?.message;
  if (errorMessageMentionsYoutube(raw)) {
    return { title: "YouTube 連結有誤", error: true };
  }
  return fallback;
};
